import { useEffect, useRef, useState, useMemo } from "react";
import axios from "axios";
import { io } from "socket.io-client";
import { useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_API_URL;
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL;

const socket = io(SOCKET_URL);

// Owner code is now verified securely by the backend before setting/changing terminal.

function Checkout() {
  const navigate = useNavigate();
  const barcodeInputRef = useRef(null);

  const token = localStorage.getItem("token")?.trim();

  const [terminalId, setTerminalId] = useState(
    localStorage.getItem("terminalId") || ""
  );

  const [terminalName, setTerminalName] = useState(
    localStorage.getItem("terminalName") || ""
  );

  const [isSettingTerminal, setIsSettingTerminal] = useState(false);
  const [inputTerminalName, setInputTerminalName] = useState("");
  const [ownerCode, setOwnerCode] = useState("");

  const [barcode, setBarcode] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  const [quickProducts, setQuickProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryProducts, setCategoryProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");

  const [cart, setCart] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [message, setMessage] = useState("");
  const [lastSale, setLastSale] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const terminalIsSet = Boolean(terminalId && terminalName);

  const authHeaders = useMemo(
    () => ({
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }),
    [token]
  );

  const cartTotal = cart.reduce(
    (sum, item) => sum + Number(item.sellingPrice) * Number(item.quantity),
    0
  );

  const focusBarcodeInput = () => {
    setTimeout(() => {
      barcodeInputRef.current?.focus();
    }, 50);
  };

  const openTerminalModal = () => {
    setInputTerminalName(terminalName || "");
    setOwnerCode("");
    setIsSettingTerminal(true);
  };

  const verifyOwnerPin = async (pin) => {
    try {
      const response = await axios.post(
        `${API_URL}/terminal/verify-owner-pin`,
        { pin },
        authHeaders
      );

      return response.data.success === true;
    } catch (error) {
      setMessage(
        error.response?.data?.message ||
        "Could not verify owner code. Please try again."
      );

      return false;
    }
  };

  const saveTerminal = async () => {
    const cleanName = inputTerminalName.trim();
    const cleanOwnerCode = ownerCode.trim();

    if (!cleanOwnerCode) {
      setMessage("Owner code is required before setting this terminal.");
      return;
    }

    if (!cleanName) {
      setMessage("Please enter a terminal name.");
      return;
    }

    const isVerified = await verifyOwnerPin(cleanOwnerCode);

    if (!isVerified) return;

    const generatedTerminalId = cleanName
      .toUpperCase()
      .replace(/\s+/g, "_")
      .replace(/[^A-Z0-9_]/g, "");

    localStorage.setItem("terminalId", generatedTerminalId);
    localStorage.setItem("terminalName", cleanName);

    setTerminalId(generatedTerminalId);
    setTerminalName(cleanName);

    setInputTerminalName("");
    setOwnerCode("");
    setIsSettingTerminal(false);
    setMessage("Terminal saved successfully.");

    focusBarcodeInput();
  };

  useEffect(() => {
    const loadQuickData = async () => {
      try {
        const [quickRes, categoriesRes] = await Promise.all([
          axios.get(`${API_URL}/products/quick-add`, authHeaders),
          axios.get(`${API_URL}/products/categories`, authHeaders),
        ]);

        setQuickProducts(quickRes.data || []);
        setCategories(categoriesRes.data || []);
      } catch (error) {
        setMessage(
          error.response?.data?.message || "Could not load checkout products."
        );
      }
    };

    if (token) {
      loadQuickData();
    }
  }, [token, authHeaders]);

  useEffect(() => {
    focusBarcodeInput();
  }, []);

  useEffect(() => {
    socket.emit("cart:update", {
      terminalId,
      terminalName,
      items: cart,
      total: cartTotal,
      paymentMethod,
      status: cart.length > 0 ? "active" : "waiting",
    });
  }, [cart, cartTotal, paymentMethod, terminalId, terminalName]);

  useEffect(() => {
    const query = searchQuery.trim();

    if (!query) {
      setSearchResults([]);
      return;
    }

    const delaySearch = setTimeout(async () => {
      try {
        setIsSearching(true);

        const res = await axios.get(
          `${API_URL}/products/search?q=${encodeURIComponent(query)}`,
          authHeaders
        );

        setSearchResults(res.data || []);
      } catch (error) {
        setMessage(error.response?.data?.message || "Search failed.");
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(delaySearch);
  }, [searchQuery, authHeaders]);

  useEffect(() => {
    const openTerminalSettings = () => {
      openTerminalModal();
    };

    window.addEventListener("open-terminal-settings", openTerminalSettings);

    return () => {
      window.removeEventListener("open-terminal-settings", openTerminalSettings);
    };
  }, []);

  const loadCategoryProducts = async (category) => {
    try {
      setSelectedCategory(category);
      setMessage("");

      const res = await axios.get(
        `${API_URL}/products/category/${encodeURIComponent(category)}`,
        authHeaders
      );

      setCategoryProducts(res.data || []);
      focusBarcodeInput();
    } catch (error) {
      setMessage(error.response?.data?.message || "Could not load category.");
    }
  };

  const addToCart = (product) => {
    setMessage("");
    setLastSale(null);

    const stockQuantity = Number(product.stockQuantity || 0);

    if (stockQuantity <= 0) {
      setMessage(`${product.name} is out of stock.`);
      focusBarcodeInput();
      return;
    }

    setCart((currentCart) => {
      const existingItem = currentCart.find(
        (item) => item.productId === product._id
      );

      if (existingItem && product.saleType === "weight") {
        setMessage(`${product.name} is already in cart. Enter weight in cart.`);
        return currentCart;
      }

      if (existingItem) {
        const newQuantity = existingItem.quantity + 1;

        if (newQuantity > stockQuantity) {
          setMessage(
            `Not enough stock for ${product.name}. Available: ${stockQuantity} ${product.unitLabel || "unit"
            }.`
          );
          return currentCart;
        }

        return currentCart.map((item) =>
          item.productId === product._id
            ? { ...item, quantity: newQuantity }
            : item
        );
      }

      return [
        ...currentCart,
        {
          productId: product._id,
          name: product.name,
          barcode: product.barcode || "",
          saleType: product.saleType || "unit",
          unitLabel: product.unitLabel || "unit",
          sellingPrice: Number(product.sellingPrice || 0),
          quantity: product.saleType === "weight" ? 0.01 : 1,
          stockQuantity,
          lowStockAlert: Number(product.lowStockAlert || 0),
        },
      ];
    });

    setSearchQuery("");
    setSearchResults([]);
    focusBarcodeInput();
  };

  const handleBarcodeSubmit = async (e) => {
    e.preventDefault();

    const cleanBarcode = barcode.trim();

    if (!cleanBarcode) return;

    try {
      const res = await axios.get(
        `${API_URL}/products/barcode/${cleanBarcode}`,
        authHeaders
      );

      addToCart(res.data);
      setBarcode("");
    } catch (error) {
      setMessage(error.response?.data?.message || "Product not found.");
      setBarcode("");
      focusBarcodeInput();
    }
  };

  const increaseQuantity = (productId, amount = 1) => {
    setCart((currentCart) =>
      currentCart.map((item) => {
        if (item.productId !== productId) return item;

        const newQuantity = Number(item.quantity) + amount;

        if (newQuantity > Number(item.stockQuantity)) {
          setMessage(
            `Not enough stock for ${item.name}. Available: ${item.stockQuantity} ${item.unitLabel}.`
          );
          return item;
        }

        setMessage("");
        return { ...item, quantity: newQuantity };
      })
    );
  };

  const decreaseQuantity = (productId, amount = 1) => {
    setCart((currentCart) =>
      currentCart
        .map((item) =>
          item.productId === productId
            ? { ...item, quantity: Number(item.quantity) - amount }
            : item
        )
        .filter((item) => item.quantity > 0)
    );

    focusBarcodeInput();
  };

  const updateWeightQuantity = (productId, value) => {
    const quantity = Number(value);

    setCart((currentCart) =>
      currentCart.map((item) => {
        if (item.productId !== productId) return item;

        if (quantity > Number(item.stockQuantity)) {
          setMessage(
            `Not enough stock for ${item.name}. Available: ${item.stockQuantity} ${item.unitLabel}.`
          );

          return { ...item, quantity: Number(item.stockQuantity) };
        }

        setMessage("");
        return { ...item, quantity: quantity < 0 ? 0 : quantity };
      })
    );
  };

  const removeItem = (productId) => {
    setCart((currentCart) =>
      currentCart.filter((item) => item.productId !== productId)
    );

    focusBarcodeInput();
  };

  const clearCart = () => {
    const confirmed = window.confirm("Clear all items from cart?");
    if (!confirmed) return;

    setCart([]);
    setMessage("");
    focusBarcodeInput();
  };

  const startNewSale = () => {
    setLastSale(null);
    setMessage("");
    focusBarcodeInput();
  };

  const getStockWarning = (item) => {
    const remainingStock =
      Number(item.stockQuantity || 0) - Number(item.quantity || 0);

    if (remainingStock <= 0) {
      return "Out of stock after this sale";
    }

    if (remainingStock <= Number(item.lowStockAlert || 0)) {
      return `Low stock: ${remainingStock} ${item.unitLabel} left`;
    }

    return "";
  };

  const completeSale = async () => {
    if (!terminalIsSet) {
      setMessage("Terminal not set. Please set this till before selling.");
      openTerminalModal();
      return;
    }

    if (cart.length === 0) {
      setMessage("Cart is empty.");
      return;
    }

    const invalidWeightedItem = cart.find(
      (item) => item.saleType === "weight" && Number(item.quantity) <= 0
    );

    if (invalidWeightedItem) {
      setMessage(`Enter valid weight for ${invalidWeightedItem.name}.`);
      return;
    }

    const overStockItem = cart.find(
      (item) => Number(item.quantity) > Number(item.stockQuantity)
    );

    if (overStockItem) {
      setMessage(
        `Not enough stock for ${overStockItem.name}. Available: ${overStockItem.stockQuantity} ${overStockItem.unitLabel}.`
      );
      return;
    }

    try {
      setIsSubmitting(true);

      const payload = {
        terminalId,
        terminalName,
        paymentMethod,
        items: cart.map((item) => ({
          productId: item.productId,
          quantity: Number(item.quantity),
        })),
      };

      const res = await axios.post(`${API_URL}/sales`, payload, authHeaders);

      setCart([]);
      setSearchResults([]);
      setSearchQuery("");
      setBarcode("");
      setLastSale(res.data.sale);
      setMessage("Sale completed successfully. Do you want to print receipt?");

      socket.emit("sale:completed", res.data.sale);

      focusBarcodeInput();
    } catch (error) {
      setMessage(error.response?.data?.message || "Sale failed.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.topBar}>
        <div>
          <h1 style={styles.title}>Checkout</h1>
          <p style={styles.subtitle}>
            {terminalIsSet
              ? `${terminalName} — Fast scan, quick add, category search.`
              : "Set this terminal before processing sales."}
          </p>
        </div>
        <div style={styles.topBarActions}>
          <button
            type="button"
            onClick={() =>
              window.open(
                "/customer-display",
                "CustomerDisplay",
                "width=1280,height=720"
              )
            }
            style={styles.customerDisplayButton}
          >
            Open Customer Display
          </button>
        </div>
        <div style={styles.topBarActions}>



        </div>
      </div>

      {message && <p style={styles.message}>{message}</p>}

      <div
        style={
          terminalIsSet
            ? styles.terminalStatusGood
            : styles.terminalStatusBad
        }
      >
        <span>
          Terminal Status:{" "}
          {terminalIsSet ? terminalName : "Not set"}
        </span>

        <button type="button" onClick={openTerminalModal} style={styles.syncButton}>
          {terminalIsSet ? "Change Terminal" : "Set Terminal"}
        </button>
      </div>

      {isSettingTerminal && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h2>{terminalIsSet ? "Change Terminal" : "Set Terminal"}</h2>

            <p style={styles.modalNote}>
              Owner code is required before this terminal can be set or changed.
            </p>

            <input
              type="password"
              placeholder="Enter owner code"
              value={ownerCode}
              onChange={(e) => setOwnerCode(e.target.value)}
              style={styles.input}
            />

            <select
              value={inputTerminalName}
              onChange={(e) => setInputTerminalName(e.target.value)}
              style={styles.input}
            >
              <option value="">Select terminal</option>
              <option value="Main Till">Main Till</option>
              <option value="Second Till">Second Till</option>
            </select>

            <button type="button" onClick={saveTerminal} style={styles.primaryButton}>
              Save Terminal
            </button>

            <button
              type="button"
              onClick={() => {
                setIsSettingTerminal(false);
                setOwnerCode("");
                setInputTerminalName("");
              }}
              style={styles.cancelButton}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {lastSale && (
        <div style={styles.receiptPrompt}>
          <div>
            <h2>Receipt?</h2>
            <p>
              Sale total: £{Number(lastSale.totalAmount).toFixed(2)} —{" "}
              {lastSale.receiptNumber}
            </p>
          </div>

          <div style={styles.receiptActions}>
            <button
              type="button"
              onClick={() => navigate(`/receipt/${lastSale._id}?autoPrint=true`)}
              style={styles.printButton}
            >
              Print Receipt
            </button>

            <button
              type="button"
              onClick={startNewSale}
              style={styles.noReceiptButton}
            >
              No Receipt / New Sale
            </button>
          </div>
        </div>
      )}

      <div style={styles.layout}>
        <div style={styles.leftPanel}>
          <form onSubmit={handleBarcodeSubmit} style={styles.scanCard}>
            <h2>Barcode Scan</h2>

            <input
              ref={barcodeInputRef}
              value={barcode}
              onChange={(e) => setBarcode(e.target.value)}
              placeholder="Scan barcode here"
              style={styles.scanInput}
              autoFocus
            />

            <button type="submit" style={styles.primaryButton}>
              Add by Barcode
            </button>
          </form>

          <div style={styles.card}>
            <h2>Quick Add</h2>

            {quickProducts.length === 0 ? (
              <p>No popular products yet.</p>
            ) : (
              <div style={styles.quickGrid}>
                {quickProducts.map((product) => (
                  <button
                    key={product._id}
                    type="button"
                    onClick={() => addToCart(product)}
                    style={styles.quickButton}
                  >
                    <strong>{product.name}</strong>
                    <span>
                      £{Number(product.sellingPrice).toFixed(2)} /{" "}
                      {product.unitLabel || "unit"}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div style={styles.card}>
            <h2>Categories</h2>

            <div style={styles.categoryGrid}>
              {categories.map((category) => (
                <button
                  key={category}
                  type="button"
                  onClick={() => loadCategoryProducts(category)}
                  style={{
                    ...styles.categoryButton,
                    ...(selectedCategory === category
                      ? styles.categoryButtonActive
                      : {}),
                  }}
                >
                  {category}
                </button>
              ))}
            </div>

            {categoryProducts.length > 0 && (
              <div style={styles.results}>
                <h3>{selectedCategory}</h3>

                {categoryProducts.map((product) => (
                  <button
                    key={product._id}
                    type="button"
                    onClick={() => addToCart(product)}
                    style={styles.resultButton}
                  >
                    <strong>{product.name}</strong>
                    <br />
                    £{Number(product.sellingPrice).toFixed(2)} per{" "}
                    {product.unitLabel || "unit"}{" "}
                    {product.saleType === "weight" ? "(weighted)" : ""}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div style={styles.card}>
            <h2>Manual Search</h2>

            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search product name, category, or barcode"
              style={styles.input}
            />

            {isSearching && <p>Searching...</p>}

            <div style={styles.results}>
              {searchResults.map((product) => (
                <button
                  key={product._id}
                  type="button"
                  onClick={() => addToCart(product)}
                  style={styles.resultButton}
                >
                  <strong>{product.name}</strong>
                  <br />
                  £{Number(product.sellingPrice).toFixed(2)} per{" "}
                  {product.unitLabel || "unit"}{" "}
                  {product.saleType === "weight" ? "(weighted)" : ""}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div style={styles.cartPanel}>
          <div style={styles.cartHeader}>
            <h2>Cart</h2>

            {cart.length > 0 && (
              <button type="button" onClick={clearCart} style={styles.clearButton}>
                Clear Cart
              </button>
            )}
          </div>

          {cart.length === 0 ? (
            <p>No items added yet.</p>
          ) : (
            cart.map((item) => (
              <div key={item.productId} style={styles.cartItem}>
                <div>
                  <strong>{item.name}</strong>
                  <p>
                    £{Number(item.sellingPrice).toFixed(2)} per {item.unitLabel}
                  </p>

                  {getStockWarning(item) && (
                    <p style={styles.stockWarning}>⚠ {getStockWarning(item)}</p>
                  )}
                </div>

                <div style={styles.qtyArea}>
                  {item.saleType === "weight" ? (
                    <>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={item.quantity}
                        onChange={(e) =>
                          updateWeightQuantity(item.productId, e.target.value)
                        }
                        style={styles.weightInput}
                      />

                      <span>{item.unitLabel}</span>
                    </>
                  ) : (
                    <>
                      <div style={styles.qtyControls}>
                        <button
                          type="button"
                          onClick={() => decreaseQuantity(item.productId)}
                          style={styles.qtyButton}
                        >
                          -
                        </button>

                        <span style={styles.qtyText}>
                          {item.quantity} {item.unitLabel}
                        </span>

                        <button
                          type="button"
                          onClick={() => increaseQuantity(item.productId)}
                          style={styles.qtyButton}
                        >
                          +
                        </button>
                      </div>

                      <div style={styles.quickQtyRow}>
                        <button
                          type="button"
                          onClick={() => increaseQuantity(item.productId, 5)}
                          style={styles.quickQtyButton}
                        >
                          +5
                        </button>

                        <button
                          type="button"
                          onClick={() => increaseQuantity(item.productId, 10)}
                          style={styles.quickQtyButton}
                        >
                          +10
                        </button>
                      </div>
                    </>
                  )}
                </div>

                <strong style={styles.lineTotal}>
                  £{(Number(item.quantity) * Number(item.sellingPrice)).toFixed(2)}
                </strong>

                <button
                  type="button"
                  onClick={() => removeItem(item.productId)}
                  style={styles.removeButton}
                >
                  Remove
                </button>
              </div>
            ))
          )}

          <div style={styles.totalBox}>
            <h1>Total: £{cartTotal.toFixed(2)}</h1>

            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              style={styles.input}
            >
              <option value="cash">Cash</option>
              <option value="card">Card</option>
            </select>

            <button
              type="button"
              onClick={completeSale}
              disabled={isSubmitting || !terminalIsSet}
              style={{
                ...styles.checkoutButton,
                ...(!terminalIsSet ? styles.disabledButton : {}),
              }}
            >
              {isSubmitting ? "Processing..." : "Complete Sale"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    padding: "24px",
    fontFamily: "Arial, sans-serif",
    background: "#f4f6f8",
    minHeight: "100vh",
  },

  topBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
  },
  topBarActions: {
    display: "flex",
    gap: "10px",
  },

  navButton: {
    padding: "12px 16px",
    borderRadius: "10px",
    border: "1px solid #ddd",
    background: "white",
    cursor: "pointer",
    fontWeight: "bold",
  },

  title: {
    margin: 0,
  },

  subtitle: {
    margin: "6px 0 0",
    color: "#555",
  },

  message: {
    padding: "12px",
    background: "#fff3cd",
    borderRadius: "8px",
    marginBottom: "16px",
  },

  terminalStatusGood: {
    padding: "12px",
    background: "#dcfce7",
    borderRadius: "8px",
    marginBottom: "16px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontWeight: "bold",
  },

  terminalStatusBad: {
    padding: "12px",
    background: "#fee2e2",
    borderRadius: "8px",
    marginBottom: "16px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontWeight: "bold",
  },

  syncButton: {
    padding: "8px 12px",
    borderRadius: "8px",
    border: "none",
    cursor: "pointer",
  },

  modal: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.4)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 999,
  },

  modalContent: {
    background: "white",
    padding: "20px",
    borderRadius: "12px",
    width: "340px",
  },

  modalNote: {
    color: "#555",
    fontSize: "14px",
    marginBottom: "12px",
  },

  cancelButton: {
    width: "100%",
    padding: "14px",
    marginTop: "10px",
    borderRadius: "10px",
    border: "1px solid #ccc",
    background: "white",
    cursor: "pointer",
  },

  receiptPrompt: {
    background: "white",
    padding: "18px",
    borderRadius: "14px",
    marginBottom: "18px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },

  receiptActions: {
    display: "flex",
    gap: "12px",
  },

  printButton: {
    padding: "14px 18px",
    borderRadius: "10px",
    border: "none",
    cursor: "pointer",
    fontSize: "16px",
  },

  noReceiptButton: {
    padding: "14px 18px",
    borderRadius: "10px",
    border: "1px solid #ccc",
    background: "white",
    cursor: "pointer",
    fontSize: "16px",
  },

  layout: {
    display: "grid",
    gridTemplateColumns: "1fr 1.6fr",
    gap: "20px",
  },

  leftPanel: {
    display: "flex",
    flexDirection: "column",
    gap: "18px",
  },

  card: {
    background: "white",
    padding: "20px",
    borderRadius: "14px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  },

  scanCard: {
    background: "#111827",
    color: "white",
    padding: "20px",
    borderRadius: "14px",
  },

  input: {
    width: "100%",
    padding: "14px",
    marginBottom: "12px",
    fontSize: "16px",
    borderRadius: "8px",
    border: "1px solid #ccc",
    boxSizing: "border-box",
  },

  scanInput: {
    width: "100%",
    padding: "18px",
    marginBottom: "12px",
    fontSize: "22px",
    borderRadius: "10px",
    border: "1px solid #ccc",
    boxSizing: "border-box",
  },

  primaryButton: {
    width: "100%",
    padding: "16px",
    fontSize: "17px",
    borderRadius: "10px",
    border: "none",
    cursor: "pointer",
  },

  quickGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "10px",
  },

  quickButton: {
    minHeight: "84px",
    padding: "14px",
    borderRadius: "12px",
    border: "1px solid #ddd",
    background: "#fafafa",
    cursor: "pointer",
    textAlign: "left",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    fontSize: "15px",
  },

  categoryGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
  },

  categoryButton: {
    padding: "12px 16px",
    borderRadius: "999px",
    border: "1px solid #ddd",
    background: "#fafafa",
    cursor: "pointer",
    fontSize: "15px",
  },

  categoryButtonActive: {
    border: "2px solid #111827",
    fontWeight: "bold",
  },

  results: {
    marginTop: "12px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },

  resultButton: {
    padding: "14px",
    textAlign: "left",
    borderRadius: "10px",
    border: "1px solid #ddd",
    cursor: "pointer",
    background: "#fafafa",
  },

  stockWarning: {
    margin: "6px 0 0",
    color: "#b45309",
    fontWeight: "bold",
    fontSize: "13px",
  },

  cartPanel: {
    background: "#bdd4f8",
    padding: "20px",
    borderRadius: "14px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    position: "sticky",
    top: "20px",
    alignSelf: "start",
  },

  cartHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },

  clearButton: {
    padding: "10px 12px",
    borderRadius: "8px",
    border: "1px solid #ccc",
    background: "white",
    cursor: "pointer",
  },

  cartItem: {
    display: "grid",
    gridTemplateColumns: "2fr 1.4fr 1fr auto",
    gap: "12px",
    alignItems: "center",
    borderBottom: "1px solid #eee",
    padding: "14px 0",
  },

  qtyArea: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },

  qtyControls: {
    display: "flex",
    gap: "10px",
    alignItems: "center",
  },

  qtyButton: {
    width: "42px",
    height: "42px",
    borderRadius: "8px",
    border: "1px solid #ddd",
    cursor: "pointer",
    fontSize: "20px",
  },

  qtyText: {
    minWidth: "60px",
    textAlign: "center",
    fontWeight: "bold",
  },

  quickQtyRow: {
    display: "flex",
    gap: "8px",
  },

  quickQtyButton: {
    padding: "8px 12px",
    borderRadius: "8px",
    border: "1px solid #ddd",
    cursor: "pointer",
  },

  weightInput: {
    width: "100px",
    padding: "12px",
    fontSize: "17px",
    borderRadius: "8px",
    border: "1px solid #ccc",
  },

  lineTotal: {
    fontSize: "18px",
  },

  removeButton: {
    padding: "10px 12px",
    cursor: "pointer",
    borderRadius: "8px",
    border: "1px solid #ddd",
  },

  totalBox: {
    marginTop: "24px",
    paddingTop: "20px",
    borderTop: "2px solid #eee",
  },

  checkoutButton: {
    width: "100%",
    padding: "22px",
    fontSize: "22px",
    fontWeight: "bold",
    borderRadius: "12px",
    border: "none",
    cursor: "pointer",
    marginTop: "12px",
  },

  disabledButton: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  topBarActions: {
  display: "flex",
  gap: "12px",
},

customerDisplayButton: {
  padding: "10px 16px",
  borderRadius: "8px",
  border: "none",
  cursor: "pointer",
  background: "#2563eb",
  color: "white",
  fontWeight: "bold",
},
};

export default Checkout;